/*
  video-in-view.js - muted, looping <video>s that are already moving when they scroll into
  view, without paying for the clips a visitor never sees.

  Opt in from markup. No `autoplay` attribute, no `poster` attribute, and preload="none":

    <video data-autoplay-in-view muted loop playsinline preload="none"> ... </video>

  Why preload="none" and no autoplay: Chrome, Android and iOS all take Shopify's HLS (.m3u8)
  source, and for HLS anything but preload="none" buffers whole segments during page load
  (a paused preload="metadata" tile pulled 3.7 MB). So the markup asks for nothing.

  Why start before the clip is on screen: on a phone a clip needs 2-3 s from play() to its
  first moving frame - iOS buffers most of an mp4 before it plays, and starts Shopify's HLS
  on its first-listed 1080p rendition. Started when it reaches the screen, a clip is still a
  frozen cover when the visitor has scrolled past it (measured in iOS Mobile Safari at 4G:
  3.8-8 s from a see-it-in-action tile appearing to its first motion).

  Timing (a clip, or a whole data-in-view-row scroller, is timed as one block):
  - START when it is on screen, within MIN_LEAD (half a viewport), or - within MAX_LEAD
    (2.5 viewports) - the current scroll speed brings it on screen within ARRIVAL_S (4 s).
  - KEEP a started clip that has not been seen yet while the visitor is still heading
    towards it: through reading pauses and the slow tail of a flick, as long as it stays
    within MAX_LEAD, the visitor has not turned away, and they moved towards it within the
    last GIVE_UP_MS (5 s). Phones scroll in flick-and-read bursts; re-testing the speed on
    every burst would start and cancel the same clip again and again.
  - CANCEL a started, never-seen clip once that no longer holds: paused, its <source>s
    detached and load() run, which aborts the transfer. pause() alone does not stop a
    download - both engines keep filling an HLS buffer after pause (1-8 MB measured).
  - A clip counts as SEEN once at least half of it has been on screen for SEEN_DWELL_MS
    (1 s); flying past does not count. A seen clip that scrolls away is paused and kept
    (coming back is instant), unless it is beyond MAX_LEAD and data is still arriving.
  - More than CANDIDATE_MARGIN (4 viewports) away, a clip is unloaded if data is still
    arriving or it was never seen; a seen clip whose download has gone quiet is kept for a
    free return. "Data still arriving" = a `progress` event within TRANSFER_QUIET_MS:
    networkState cannot tell, it stays LOADING for HLS in both engines.
  - Rows (mark a horizontal scroller with data-in-view-row): a tile at least half visible
    in the row is timed with the row; another tile starts only once at least a quarter of
    it is in view inside the row while the row is on screen - i.e. a real swipe is bringing
    it in (the peeking sliver of the next tile is less than that). Taps and vertical page
    scrolls that start on the row do not count.
  - A clip the visitor started (data-user-played, set by the section's own play control) is
    not paused for timing reasons, only once it has been fully off screen for
    USER_OFFSCREEN_MS, and it resumes when it comes back. data-user-paused means the visitor
    paused it: never restarted or unloaded until the section removes it. The section calls
    videoInView.restore(video) before its own play().
  - Nothing starts before the page has finished loading (window load, or LOAD_CAP_MS at
    most), so a clip never competes with the hero image for bandwidth.
  - Never autoplays under prefers-reduced-motion or Save-Data (a visitor-started clip
    still plays). The clip stays on its cover and the section's own play control is the way
    in. (Safari does not expose Save-Data, so iOS Low Data Mode is not detected here; if it
    refuses autoplay, that arrives as a refused play() like Low Power Mode.)
  - data-in-view-tap-start: for a clip with no play control of its own (homepage-mission).
    A refused start (iOS Low Power Mode refuses muted autoplay) is retried inside the
    visitor's next tap anywhere on the page while the clip is on screen - a user gesture may
    start playback. Not for clips that have their own play control (see-it-in-action
    tiles): a tap on the tile would then start the clip twice over. Touch only, so a
    desktop "never autoplay" setting is respected.
  - Cover image: an element with data-video-poster beside the <video> (same parent) fades
    out (--dur-slow) once the clip has presented a frame, then gets `hidden`; it comes back
    when the clip is unloaded. Use a lazy <img data-video-poster> instead of a `poster`
    attribute, which browsers fetch at parse time wherever the video sits. Its CSS needs a
    [hidden] { display: none } override (CLAUDE.md, [hidden]). A section with its own cover
    logic can listen for the video's `playing` and `emptied` events instead.
  - Idempotent. Claimed videos carry data-autoplay-observed. Re-run
    window.videoInView.scan(root) after swapping markup in, e.g. a Section Rendering API
    re-render; videos (and rows) that have left the DOM are released - and their downloads
    stopped - on the next scan. Theme-editor section reloads re-scan on their own.

  Never call load() on a clip that still has its sources and is not about to play: on a
  preload="none" clip it buffers the whole file even when play() is then refused.

  Load it with `defer` from each section that uses it. A second copy on the page is a no-op.
*/
(function () {
  'use strict';

  if (window.videoInView) return;

  var SELECTOR = 'video[data-autoplay-in-view]:not([data-autoplay-observed])';
  /* Clips (or rows) within this distance of the screen are tracked; beyond it they are
     unloaded if data is still arriving or they were never seen. */
  var CANDIDATE_MARGIN = '400% 0px';
  /* Start a clip that the current scroll speed brings on screen within this many seconds... */
  var ARRIVAL_S = 4;
  /* ...as long as it is within this many viewports of the screen... */
  var MAX_LEAD = 2.5;
  /* ...or that is within this share of a viewport, whatever the speed. */
  var MIN_LEAD = 0.5;
  /* A started, never-seen clip is kept while the visitor moved towards it this recently. */
  var GIVE_UP_MS = 5000;
  /* Scroll speeds below this (px/s) count as standing still, not as turning away. */
  var MOVE_EPS = 40;
  /* Half of a clip on screen for this long marks it seen. */
  var SEEN_DWELL_MS = 1000;
  /* No `progress` event for this long means the download has gone quiet. */
  var TRANSFER_QUIET_MS = 2000;
  /* A visitor-started clip fully off screen for this long is paused (and resumed on return). */
  var USER_OFFSCREEN_MS = 1000;
  /* Scroll speed is measured over this window... */
  var VELOCITY_WINDOW_MS = 150;
  /* ...and no scroll for this long means standing still (longer than a finger re-grab). */
  var STILL_MS = 400;
  /* Row tiles: share in view inside the row to be timed with the row / to count as swiped in. */
  var ROW_PRIMARY_SHARE = 0.5;
  var ROW_ENTER_SHARE = 0.25;
  /* Cover fade: give up waiting for a presented frame after this long and fade anyway. */
  var FRAME_WAIT_MS = 1000;
  /* Cover fade: grace added to the computed transition before `hidden` is set anyway, for
     when transitionend never fires (tab hidden, transition removed). */
  var FADE_GRACE_MS = 200;
  /* Gesture retry for refused tap-start clips: touch only (see the header). */
  var GESTURES = ['touchend'];
  /* Longest wait for window load before clips may start anyway. */
  var LOAD_CAP_MS = 4000;

  var reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var passiveCapture = { capture: true, passive: true };
  var candidateObserver = null;
  var screenObserver = null;
  var tracked = [];
  var rows = [];
  var fallback = [];
  var gestureArmed = false;
  var pageLoaded = document.readyState === 'complete';
  var velocity = 0;
  var samples = [];
  var frameQueued = false;
  var stillTimer = 0;

  function autoplayAllowed() {
    if (reducedMotion && reducedMotion.matches) return false;
    var connection = navigator.connection;
    return !(connection && connection.saveData);
  }

  function now() {
    return performance.now();
  }

  /* ---- Scroll speed ---- */

  function onScroll() {
    var time = now();
    samples.push({ t: time, y: window.scrollY });
    while (samples.length > 2 && time - samples[1].t >= VELOCITY_WINDOW_MS) samples.shift();
    var first = samples[0];
    velocity = time > first.t ? (window.scrollY - first.y) / ((time - first.t) / 1000) : 0;
    clearTimeout(stillTimer);
    stillTimer = setTimeout(function () {
      velocity = 0;
      samples = [];
      queueUpdate();
    }, STILL_MS);
    queueUpdate();
  }

  function queueUpdate() {
    if (frameQueued) return;
    frameQueued = true;
    requestAnimationFrame(function () {
      frameQueued = false;
      tracked.forEach(update);
    });
  }

  /* ---- Geometry: where is the clip (or its row) relative to the screen? ---- */

  function geometry(el) {
    var rect = el.getBoundingClientRect();
    var height = window.innerHeight || document.documentElement.clientHeight;
    var g = { height: height, onScreen: false, gap: 0, towards: 0 };
    if (rect.top >= height) {
      g.gap = rect.top - height;
      g.towards = velocity;
    } else if (rect.bottom <= 0) {
      g.gap = -rect.bottom;
      g.towards = -velocity;
    } else {
      g.onScreen = true;
    }
    if (g.onScreen || g.towards > MOVE_EPS) el.inViewApproachAt = now();
    return g;
  }

  /* The visitor is about to see it: on screen, within MIN_LEAD, or within MAX_LEAD and due
     on screen within ARRIVAL_S at the current speed. */
  function arriving(g) {
    if (g.onScreen || g.gap <= g.height * MIN_LEAD) return true;
    return g.gap <= g.height * MAX_LEAD && g.towards > MOVE_EPS && g.gap / g.towards < ARRIVAL_S;
  }

  /* A started, never-seen clip: is the visitor still on the way to it? */
  function stillComing(g, el) {
    return g.gap <= g.height * MAX_LEAD && g.towards >= -MOVE_EPS &&
      now() - (el.inViewApproachAt || 0) < GIVE_UP_MS;
  }

  function transferring(video) {
    return now() - (video.inViewLastProgress || 0) < TRANSFER_QUIET_MS;
  }

  /* ---- What should this clip do right now? ---- */

  function isCandidate(video) {
    var row = video.inViewRow;
    return row ? !!row.inViewCandidate : !!video.inViewCandidate;
  }

  function wanted(video) {
    var row = video.inViewRow;
    var timing = row || video;
    if (row) {
      var share = video.inViewRowShare || 0;
      if (share < ROW_PRIMARY_SHARE) return share >= ROW_ENTER_SHARE && !!row.inViewOnScreen;
    }
    var g = geometry(timing);
    if (arriving(g)) return true;
    /* Hysteresis: a started clip the visitor has not reached yet stays while they keep coming. */
    return !video.inViewSeen && !!video.dataset.inViewRequested && stillComing(g, timing);
  }

  function update(video) {
    if (!video.isConnected) return;

    if (!isCandidate(video)) {
      if (!video.paused) video.pause();
      if (transferring(video) || !video.inViewSeen) unload(video);
      return;
    }

    /* The visitor started it: pause it only once it has been fully off screen for a moment. */
    if (video.hasAttribute('data-user-played')) {
      updateUserPlayed(video);
      return;
    }

    if (wanted(video)) {
      start(video);
      return;
    }

    if (!video.paused) video.pause();
    if (!video.dataset.inViewRequested) return;
    if (!video.inViewSeen) {
      /* Started, never seen, and the visitor is not coming after all. */
      unload(video);
    } else if (!video.inViewOnScreen && transferring(video) && geometry(video.inViewRow || video).gap > window.innerHeight * MAX_LEAD) {
      unload(video);
    }
  }

  function updateUserPlayed(video) {
    if (video.inViewOnScreen) {
      clearTimeout(video.inViewOffTimer);
      video.inViewOffTimer = 0;
      if (video.paused && video.inViewPausedOffscreen) {
        video.inViewPausedOffscreen = false;
        start(video);
      }
      return;
    }
    if (!video.paused && !video.inViewOffTimer) {
      video.inViewOffTimer = setTimeout(function () {
        video.inViewOffTimer = 0;
        if (!video.inViewOnScreen && !video.paused) {
          video.inViewPausedOffscreen = true;
          video.pause();
        }
      }, USER_OFFSCREEN_MS);
    }
  }

  /* ---- Unload / restore: the only way to stop a download that pause() leaves running ---- */

  function unload(video) {
    if (!video.dataset.inViewRequested || video.inViewSources) return;
    if (video.hasAttribute('data-user-paused')) return;
    /* Mid-startup: cutting the sources now can leave WebKit's HLS stack fetching segments for
       the detached player. Finish when play() settles instead. */
    if (video.dataset.inViewPending) {
      video.inViewUnloadWhenSettled = true;
      if (!video.paused) video.pause();
      return;
    }
    var sources = Array.prototype.slice.call(video.querySelectorAll('source'));
    if (!sources.length) return;
    if (!video.paused) video.pause();
    video.inViewSources = sources;
    sources.forEach(function (source) {
      video.removeChild(source);
    });
    /* No source left: load() aborts the fetch and frees the decoder (fires `emptied`). */
    video.load();
    delete video.dataset.inViewRequested;
    video.inViewLastProgress = 0;
  }

  function restore(video) {
    video.inViewUnloadWhenSettled = false;
    var sources = video.inViewSources;
    if (!sources) return;
    video.inViewSources = null;
    /* Inserting a <source> into a sourceless video runs resource selection on its own; with
       preload="none" nothing is fetched until the play() that follows. */
    sources.forEach(function (source) {
      video.appendChild(source);
    });
  }

  /* ---- Gesture retry (data-in-view-tap-start only) ---- */

  function onGesture() {
    tracked.forEach(function (video) {
      if (!video.dataset.inViewBlocked || !video.isConnected || !video.paused) return;
      if (!video.inViewOnScreen || video.hasAttribute('data-user-paused')) return;
      delete video.dataset.inViewBlocked;
      start(video);
    });
    if (!tracked.some(function (video) { return video.dataset.inViewBlocked; })) {
      GESTURES.forEach(function (type) {
        document.removeEventListener(type, onGesture, passiveCapture);
      });
      gestureArmed = false;
    }
  }

  function armGesture() {
    if (gestureArmed) return;
    gestureArmed = true;
    GESTURES.forEach(function (type) {
      document.addEventListener(type, onGesture, passiveCapture);
    });
  }

  /* ---- Start ---- */

  function start(video) {
    if (!pageLoaded) return;
    if (!video.paused || video.dataset.inViewPending) return;
    if (video.hasAttribute('data-user-paused')) return;
    if (!autoplayAllowed() && !video.hasAttribute('data-user-played')) return;
    /* Refused by policy: wait for a gesture (tap-start) or a fresh entry, not every scroll. */
    if (video.dataset.inViewBlocked) return;

    restore(video);

    /* inViewPending covers the gap before play() settles, so a second start in that window
       cannot start the same clip twice. inViewRequested marks a clip whose download may be
       running, i.e. one that unload() may cancel (also set by the `play` event, so a clip
       the section started counts too). */
    video.dataset.inViewPending = 'true';
    video.dataset.inViewRequested = 'true';
    video.inViewApproachAt = now();
    var played = video.play();

    function settle() {
      delete video.dataset.inViewPending;
      if (video.inViewUnloadWhenSettled) {
        video.inViewUnloadWhenSettled = false;
        unload(video);
      }
    }

    /* Older browsers return undefined rather than a promise. NotAllowedError = refused by
       policy (Low Power Mode): nothing is downloading; a tap-start clip waits for a tap,
       any other clip for its own play control. Any other rejection (AbortError from a pause
       or unload) leaves the clip observed. */
    if (played && played.then) {
      played.then(function () {
        delete video.dataset.inViewBlocked;
        settle();
      }, function (error) {
        if (error && error.name === 'NotAllowedError') {
          delete video.dataset.inViewRequested;
          video.dataset.inViewBlocked = 'true';
          if (video.hasAttribute('data-in-view-tap-start')) armGesture();
        }
        settle();
      });
    } else {
      settle();
    }
  }

  /* ---- Observers ---- */

  /* root: document, so the candidate margin also reaches past the viewport inside an iframe
     (the theme editor): with the implicit root the iframe's own viewport clips the target
     and rootMargin cannot extend beyond it. Engines that reject a Document root fall back. */
  function getCandidateObserver() {
    if (!candidateObserver) {
      var onEntries = function (entries) {
        entries.forEach(function (entry) {
          entry.target.inViewCandidate = entry.isIntersecting;
        });
        queueUpdate();
      };
      try {
        candidateObserver = new IntersectionObserver(onEntries, { root: document, rootMargin: CANDIDATE_MARGIN });
      } catch (error) {
        candidateObserver = new IntersectionObserver(onEntries, { rootMargin: CANDIDATE_MARGIN });
      }
    }
    return candidateObserver;
  }

  /* On screen: feeds the tap retry; a refused clip gets one fresh try each time it comes back
     on screen; half of it on screen for SEEN_DWELL_MS marks it seen. */
  function getScreenObserver() {
    if (!screenObserver) {
      screenObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var target = entry.target;
          if (target.tagName === 'VIDEO') {
            if (entry.isIntersecting && !target.inViewOnScreen && !target.hasAttribute('data-in-view-tap-start')) {
              delete target.dataset.inViewBlocked;
            }
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              if (!target.inViewSeen && !target.inViewSeenTimer) {
                target.inViewSeenTimer = setTimeout(function () {
                  target.inViewSeenTimer = 0;
                  target.inViewSeen = true;
                }, SEEN_DWELL_MS);
              }
            } else {
              clearTimeout(target.inViewSeenTimer);
              target.inViewSeenTimer = 0;
            }
          }
          target.inViewOnScreen = entry.isIntersecting;
        });
        queueUpdate();
      }, { threshold: [0, 0.5] });
    }
    return screenObserver;
  }

  function setUpRow(row) {
    if (row.inViewRowVideos) return row;
    row.inViewRowVideos = [];
    row.inViewShareObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        entry.target.inViewRowShare = entry.isIntersecting ? entry.intersectionRatio : 0;
      });
      queueUpdate();
    }, { root: row, threshold: [0, ROW_ENTER_SHARE, ROW_PRIMARY_SHARE] });
    getCandidateObserver().observe(row);
    getScreenObserver().observe(row);
    rows.push(row);
    return row;
  }

  function releaseRow(row) {
    row.inViewShareObserver.disconnect();
    getCandidateObserver().unobserve(row);
    getScreenObserver().unobserve(row);
    row.inViewRowVideos = null;
    row.inViewShareObserver = null;
  }

  /* ---- Cover image ---- */

  function showPoster(poster) {
    poster.inViewFade = (poster.inViewFade || 0) + 1;
    poster.style.transition = 'none';
    poster.style.opacity = '';
    poster.hidden = false;
  }

  /* Fade the cover out once the clip has PRESENTED a frame - `playing` can fire a frame or
     two before anything is painted, and cutting the cover then flashes the empty video box.
     requestVideoFrameCallback reports the first presented frame (Chrome, Safari 15.4+);
     older browsers wait two animation frames. Linear, not --ease-out: the ease front-loads
     58% of the change into the first 50 ms, which reads as a flash rather than a dissolve. */
  function fadePoster(poster) {
    if (poster.hidden) return;
    var fade = (poster.inViewFade || 0) + 1;
    poster.inViewFade = fade;
    poster.style.transition = 'opacity var(--dur-slow) linear';
    poster.style.opacity = '0';

    /* The fallback follows the transition actually applied (--dur-slow today), so a longer
       token never has its fade cut short. getComputedStyle reports it in seconds. */
    var duration = parseFloat(getComputedStyle(poster).transitionDuration) || 0;
    var timer = 0;
    function done() {
      clearTimeout(timer);
      poster.removeEventListener('transitionend', done);
      if (poster.inViewFade === fade) poster.hidden = true;
    }
    poster.addEventListener('transitionend', done);
    timer = setTimeout(done, duration * 1000 + FADE_GRACE_MS);
  }

  function watchPoster(video) {
    var poster = video.parentNode && video.parentNode.querySelector('[data-video-poster]');
    if (!poster) return;
    video.addEventListener('playing', function () {
      if (poster.hidden) return;
      var fade = poster.inViewFade;
      function presented() {
        clearTimeout(waited);
        if (poster.inViewFade === fade) fadePoster(poster);
      }
      var waited = setTimeout(function () {
        if (!video.paused) presented();
      }, FRAME_WAIT_MS);
      if (video.requestVideoFrameCallback) {
        video.requestVideoFrameCallback(presented);
      } else {
        requestAnimationFrame(function () {
          requestAnimationFrame(presented);
        });
      }
    });
    /* Unloaded: the frame is gone, so the cover comes back until the next start. */
    video.addEventListener('emptied', function () {
      showPoster(poster);
    });
  }

  /* ---- Per-video media listeners ---- */

  function watchMedia(video) {
    /* Any start counts, including the section's own play() for a visitor tap. */
    video.addEventListener('play', function () {
      video.dataset.inViewRequested = 'true';
    });
    video.addEventListener('progress', function () {
      video.inViewLastProgress = now();
      /* Bytes still arriving for an unloaded player: make sure the fetch really stops. */
      if (video.inViewSources) {
        video.removeAttribute('src');
        video.load();
        return;
      }
      /* Seen, paused, off screen and beyond MAX_LEAD, yet still downloading (WebKit keeps
         fetching renditions after pause): stop it. */
      if (video.inViewSeen && video.paused && !video.inViewOnScreen && !video.hasAttribute('data-user-paused')) {
        queueUpdate();
      }
    });
  }

  /* ---- Scan ---- */

  /* A swapped-out grid leaves detached <video>s (and rows) behind: stop their downloads and
     stop watching them. */
  function releaseDetached() {
    tracked = tracked.filter(function (video) {
      if (video.isConnected) return true;
      clearTimeout(video.inViewOffTimer);
      clearTimeout(video.inViewSeenTimer);
      video.removeAttribute('data-user-paused');
      unload(video);
      getScreenObserver().unobserve(video);
      var row = video.inViewRow;
      if (row && row.inViewRowVideos) {
        row.inViewShareObserver.unobserve(video);
        row.inViewRowVideos = row.inViewRowVideos.filter(function (other) {
          return other !== video;
        });
      } else {
        getCandidateObserver().unobserve(video);
      }
      return false;
    });
    rows = rows.filter(function (row) {
      if (row.isConnected && row.inViewRowVideos.length) return true;
      releaseRow(row);
      return false;
    });
  }

  function scan(root) {
    releaseDetached();

    var scope = root && root.querySelectorAll ? root : document;
    var videos = scope.querySelectorAll(SELECTOR);

    Array.prototype.forEach.call(videos, function (video) {
      video.setAttribute('data-autoplay-observed', '');
      watchPoster(video);
      watchMedia(video);

      /* No IntersectionObserver (very old engines): just start it once the page has loaded. */
      if (!('IntersectionObserver' in window)) {
        fallback.push(video);
        if (pageLoaded) start(video);
        return;
      }

      var row = video.closest('[data-in-view-row]');
      if (row) {
        video.inViewRow = setUpRow(row);
        row.inViewRowVideos.push(video);
        row.inViewShareObserver.observe(video);
      } else {
        getCandidateObserver().observe(video);
      }
      getScreenObserver().observe(video);
      tracked.push(video);
    });

    if (tracked.length) window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* restore(video): a section's own play control calls this before play(), in case the clip
     was unloaded. */
  window.videoInView = { scan: scan, restore: restore };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }

  /* Starts wait for window load (or LOAD_CAP_MS, if a slow third party holds `load` up). */
  function onPageLoaded() {
    if (pageLoaded) return;
    pageLoaded = true;
    fallback.forEach(start);
    queueUpdate();
  }
  if (!pageLoaded) {
    window.addEventListener('load', onPageLoaded, { once: true });
    setTimeout(onPageLoaded, LOAD_CAP_MS);
  }

  /* Theme editor: a re-rendered section brings fresh <video>s that nothing is watching. */
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
})();
